import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { orderBy, uniqBy } from 'lodash';
import * as moment from 'moment';
import { Answer, Inspection, InspectionItem, ItemInfo, User } from '../../shared/interfaces';
import { UserService } from '../../user/user.service';
import { VersionService } from '../../version/version.service';
import { InspectionService } from '../inspection.service';

export interface AllNote {
  id?: number;
  inspectionId?: number;
  date?: Date;
  type: string;
  note: string;
  user?: User | string;
  itemIndex?: number;
  sectionIndex?: number;
  questionIndex?: number;
  questionId?: number;
  groupId?: number;
  flagId?: number;
}

@Component({
  selector: 'app-inspection-notes',
  templateUrl: './inspection-notes.component.html',
  styleUrls: ['./inspection-notes.component.scss'],
})
export class InspectionNotesComponent implements OnInit {
  constructor(
    public inspectionService: InspectionService,
    public userService: UserService,
    public route: ActivatedRoute,
    private router: Router,
    private versionService: VersionService,
  ) {}

  inspection: Inspection;
  answers: Answer[];
  items: Array<InspectionItem & { info?: ItemInfo; index?: number }> = [];

  allNotes: AllNote[] = [];
  filteredNotes: AllNote[] = [];
  selectedTypes = [];

  noteTypes: any[] = [
    {
      label: 'Inspection created Note (only visible to inspector)',
      type: 'inspection',
      count: 0,
    },
    {
      label: 'Question Comments/Notes',
      type: 'answer',
      count: 0,
    },
    {
      label: 'Flag Notes',
      type: 'flag',
      count: 0,
    },
    {
      label: 'Flag Group Internal Notes',
      type: 'flag-group',
      count: 0,
    },
    {
      label: 'Flag Internal Notes',
      type: 'flag-internal',
      count: 0,
    },
    {
      label: 'Underwriting Review Comments',
      type: 'flag-review',
      count: 0,
    },
  ];

  allFlags = [];
  allComments = [];

  @Output() noteClicked = new EventEmitter<void>();

  ngOnInit() {
    this.route.params.subscribe(async params => {
      // Load data
      this.inspection = await this.inspectionService.get(params.id);
      this.answers = await this.inspectionService.answers(this.inspection.id);
      await this.loadItems();
      await this.mergeAnswers();
      await this.getFlagsAndComments();

      // Create notes
      await this.createInspectionNote();
      this.createAnswerNotes();
      this.createFlagNotes();
      this.createFlagGroupNotes();
      this.createUnderwritingReviewNotes();

      this.allNotes = orderBy(this.allNotes, ['date'], ['asc']);
      this.filteredNotes = this.allNotes;
      this.noteTypes.forEach(type => {
        type.count = this.allNotes.filter(x => x.type === type.type && x.note !== null && x.note !== '').length;
      });
    });
  }

  // Load data

  async loadItems() {
    this.items = [];
    const items = (await this.versionService.tree(this.inspection.versionId)).items;
    // filter empty sections
    items.forEach(item => {
      item.sections = item.sections.filter(section => section.questions.length);
    });
    // filter empty items
    items.forEach((item: any, i) => {
      if (!item.sections.length) {
        delete items[i];
      } else {
        item.info = this.inspection.items.find(info => info.itemId === item.id);
      }
    });
    items.forEach((item: InspectionItem & { info: ItemInfo }) => {
      if (!item.info) {
        return;
      }
      if (item.info.quantity === 1) {
        this.items.push({
          ...item,
        });
      } else if (item.info.quantity > 1) {
        for (let i = 0; i < item.info.quantity; i++) {
          this.items.push({
            ...JSON.parse(JSON.stringify(item)),
            index: i + 1,
          });
        }
      }
    });
  }

  // tslint:disable:forin
  mergeAnswers() {
    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;
      const buildingIndex = item.index;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          if (question.repeat && question.repeat.length) {
            question.repeat.forEach(() => {
              question.answer = this.answers.filter(ans => {
                return ans.questionId === question.id;
              });
            });
          } else {
            const answer = this.answers.find(ans => {
              const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
              return ans.questionId === question.id && indexOk;
            });
            if (answer) {
              question.answer = answer;
            }
          }
          const answers = Array.isArray(question.answer) ? question.answer : [question.answer];
          answers.forEach(answer => {
            if (answer) {
              answer.question = question;
              // set flags & comments' deeplink
              [...answer.flags, ...answer.comments].forEach((x: any) => {
                x.deeplink = [+iItem, +iSection, +iQuestion];
              });
            }
          });
        }
      }
    }
  }

  // tslint:disable:forin
  getFlagsAndComments() {
    this.allFlags = [];
    this.allComments = [];

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        const sectionFlags = [];
        const sectionComments = [];

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          const flags = this.getFlags(question);
          const comments = this.getComments(question);

          sectionFlags.push(
            ...flags.map(f => ({
              ...f,
              flag: f,
              question: question.text,
            })),
          );

          sectionComments.push(
            ...comments.map(f => ({
              ...f,
              question: question.text,
            })),
          );
        }

        if (sectionFlags.length) {
          this.allFlags.push(sectionFlags);
        }

        if (sectionComments.length) {
          this.allComments.push(sectionComments);
        }
      }
    }
  }

  // Create Notes

  async createInspectionNote() {
    const inspectionChanges = await this.inspectionService.changes(this.inspection.id);

    const firstChanged = moment(inspectionChanges[0].date).format('MM-DD-YYYY');
    let createdBy = inspectionChanges[0].username;

    if (firstChanged) {
      createdBy = inspectionChanges[0].username;
    }

    for (const change of inspectionChanges) {
      if (change.changes && change.changes.notes) {
        createdBy = change.username;
      }
    }
    this.allNotes.push({
      inspectionId: this.inspection.id,
      date: this.inspection.createdAt,
      type: 'inspection',
      note: this.inspection.notes,
      user: createdBy,
    });
  }

  createAnswerNotes() {
    for (const comments of this.allComments) {
      for (const comment of comments) {
        const index = comment.deeplink;
        let commentText = comment.text === ' ' ? null : comment.text;
        if (comment.qIndex) {
          commentText = `${commentText} - Building #${comment.qIndex}`;
        }
        this.allNotes.push({
          inspectionId: this.inspection.id,
          date: comment.date,
          type: 'answer',
          note: commentText,
          user: comment.username,
          itemIndex: index[0],
          sectionIndex: index[1],
          questionIndex: index[2],
          questionId: comment.questionId,
        });
      }
    }
  }

  createFlagNotes() {
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        const index = flag.deeplink;

        if (flag.text && flag.text.length > 0) {
          let flagDate = flag.date;
          const flagUser = flag.user ? flag.user.firstName + ' ' + flag.user.lastName : 'Auto flag';
          const flagChanges = flag.changes;
          for (const change of flagChanges) {
            if (change.changes && change.changes.Text) {
              flagDate = change.date;
            }
          }

          this.allNotes.push({
            inspectionId: this.inspection.id,
            date: flagDate,
            type: 'flag',
            note: flag.text,
            user: flagUser,
            itemIndex: index[0],
            sectionIndex: index[1],
            questionIndex: index[2],
            questionId: flag.questionId,
          });
        }
      }
    }
  }

  createFlagGroupNotes() {
    const flagsWithViolation = [];
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        if (flag.violation.internalNotes && flag.violation.internalNotes.length > 0) {
          flagsWithViolation.push({
            ...flag,
          });
        }
      }
    }
    const flagsWithGroup = flagsWithViolation.filter(f => f.groupId);
    const flagsWithoutGroup = flagsWithViolation.filter(f => !f.groupId);

    const uniqueFlagsWithGroup = uniqBy(flagsWithGroup, flag => `${flag.groupId}_${flag.violation.internalNotes}`);

    const allFlags = [...uniqueFlagsWithGroup, ...flagsWithoutGroup];

    for (const flag of allFlags) {
      let flagDate = flag.date;
      const flagUser = flag.user ? flag.user.firstName + ' ' + flag.user.lastName : 'Auto flag';
      const flagChanges = flag.changes;
      for (const change of flagChanges) {
        if (
          change.changes &&
          change.changes['Internal Notes'] &&
          change.changes['Internal Notes'].after &&
          change.changes['Internal Notes'].after.length > 0
        ) {
          flagDate = change.date;
        }
      }
      this.allNotes.push({
        inspectionId: this.inspection.id,
        date: flagDate,
        type: flag.groupId ? 'flag-group' : 'flag-internal',
        note: flag.violation.internalNotes,
        user: flagUser,
        flagId: flag.id,
        groupId: flag.groupId || null,
      });
    }
  }

  createUnderwritingReviewNotes() {
    for (const flags of this.allFlags) {
      for (const flag of flags) {
        if (flag.comments && flag.comments.length > 0) {
          this.allNotes.push({
            inspectionId: this.inspection.id,
            date: flag.comments[0].date,
            type: 'flag-review',
            note: flag.comments[0].text,
            user: flag.comments[0].username,
            flagId: flag.id,
          });
        }
      }
    }
  }

  // Filter

  filterNotes() {
    if (this.selectedTypes.length === 0) {
      this.filteredNotes = [...this.allNotes];
    } else {
      this.filteredNotes = this.allNotes.filter(note => {
        return this.selectedTypes.includes(note.type);
      });
    }
  }

  // Utils functions

  getComments(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        let i = 1;
        return q.answer.reduce(
          (acc, cur) => [...acc, ...cur.comments.map(c => ({ ...c, questionId: q.id, qIndex: i++ }))],
          [],
        );
      } else {
        return q.answer.comments.map(c => ({ ...c, questionId: q.id }));
      }
    }
    return [];
  }

  getFlags(q) {
    // Ignoring related
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce(
          (acc, ans) => [
            ...acc,
            ...ans.flags.filter(f => f.answerId === ans.id).map(f => ({ ...f, questionId: q.id })),
          ],
          [],
        );
      } else {
        return q.answer.flags.filter(f => f.answerId === q.answer.id).map(f => ({ ...f, questionId: q.id }));
      }
    }
    return [];
  }

  formatDate(date: Date) {
    return moment(date).format('MM-DD-YYYY');
  }

  scrollToNotes() {
    this.noteClicked.emit();
  }
}
